import { type JSX } from 'react';
import { useRouter } from 'next/router';
import { BannerCard, APYText } from '@/shared/components/BannerCard';
import Button from '@/shared/components/flip-ui-kit/Button';
import { TABLE_POLL_INTERVAL } from '@/shared/constants';
import useEvmScAccount from '@/shared/hooks/useEvmScAccount';
import { compareDelegationApy, FALLBACK_ADDTL_DELEGATION } from '@/shared/utils/apy';
import { FLIP_SYMBOL } from '@/shared/utils/env';
import TokenAmount from '@/shared/utils/TokenAmount';
import Delegate from '../../../assets/svg/delegate-flip.svg';
import useOperatorsAndAuction, {
  type OperatorSummary,
} from '../../../hooks/useOperatorsAndAuction';

export function DelegateBannerCard(): JSX.Element {
  const router = useRouter();
  const { evmFlipBalance } = useEvmScAccount();
  const clamped = evmFlipBalance?.max(TokenAmount.ZERO)?.min(FALLBACK_ADDTL_DELEGATION);
  const additionalDelegation =
    clamped && clamped.gt(TokenAmount.ZERO) ? clamped : FALLBACK_ADDTL_DELEGATION;

  // fetching all the operators
  const { operators = [], isLoading } = useOperatorsAndAuction({
    refetchInterval: TABLE_POLL_INTERVAL,
  });

  const delegatorApy =
    operators
      .filter((o) => o.isDelegationAllowed)
      .reduce<OperatorSummary | null>((acc, other) => {
        if (!acc) return other;

        return compareDelegationApy(acc.nextEpoch.delegatorApy, other.nextEpoch.delegatorApy) === 1
          ? acc
          : other;
      }, null)
      ?.nextEpoch.calculateDelegatorApy(additionalDelegation) ?? 0;

  return (
    <BannerCard
      header={`Delegate your ${FLIP_SYMBOL}`}
      body={<APYText value={delegatorApy} prefix suffix />}
      isBodyLoading={isLoading}
      action={
        <Button
          type="secondary-standard"
          size="small"
          className="border-cf-gray-5 bg-cf-gray-4 px-2 py-3 hover:enabled:text-cf-white"
          onClick={() => {
            router.push('/delegate');
          }}
        >
          Delegate now
        </Button>
      }
      bannerImg={<Delegate className="h-full w-full" />}
      headerClassName="text-cf-light-3"
    />
  );
}
